import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Crown, Check, Sparkles, Loader2, ArrowRight, Camera, Activity, Flame, Apple } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useBilling } from "../context/BillingContext";
import { isNativeApp } from "../lib/platform";
import {
  fetchApplePrice,
  purchasePro,
  restoreApplePurchases,
  getIapServerStatus,
} from "../lib/applePurchase";

const BENEFITS = [
  { Icon: Activity, label: "Unlimited AI workout plans (1–12 weeks)" },
  { Icon: Camera,   label: "Photo meal logging powered by Claude Vision" },
  { Icon: Flame,    label: "AI micro-workouts from the muscle heatmap" },
  { Icon: Apple,    label: "AI text-to-meal estimator" },
  { Icon: Sparkles, label: "All future Pro-tier features included" },
];

export default function BillingPage() {
  const { sub, refresh, loading } = useBilling();
  const [params, setParams] = useSearchParams();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [pollMessage, setPollMessage] = useState("");

  const sessionIdFromUrl = params.get("session_id");
  const canceled = params.get("canceled") === "1";

  // Poll for payment status when returning from Stripe.
  useEffect(() => {
    if (!sessionIdFromUrl) return;
    let attempts = 0;
    let active = true;
    setPolling(true);
    setPollMessage("Confirming payment…");

    const poll = async () => {
      if (!active) return;
      try {
        const { data } = await api.get(`/billing/checkout-status/${sessionIdFromUrl}`);
        if (data.payment_status === "paid") {
          await refresh();
          setPollMessage("");
          setPolling(false);
          toast.success("Welcome to DAIONE Pro 🎉");
          // Clean the URL.
          setParams({});
          return;
        }
        if (data.status === "expired") {
          setPollMessage("Checkout session expired. Try again.");
          setPolling(false);
          return;
        }
        attempts += 1;
        if (attempts >= 6) {
          setPollMessage("Still processing — refresh in a moment or contact support.");
          setPolling(false);
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        setPollMessage(err.response?.data?.detail || "Could not confirm payment");
        setPolling(false);
      }
    };
    poll();
    return () => { active = false; };
  }, [sessionIdFromUrl, refresh, setParams]);

  useEffect(() => {
    if (canceled) {
      toast.message("Checkout canceled — no charge made.");
      setParams({});
    }
  }, [canceled, setParams]);

  const native = isNativeApp();

  // Apple IAP state (only meaningful when running inside the iOS app).
  const [iapServerReady, setIapServerReady] = useState(null); // null = unknown
  const [applePrice, setApplePrice] = useState(null);
  const [iapLoading, setIapLoading] = useState(false);

  useEffect(() => {
    if (!native) return;
    let cancelled = false;
    (async () => {
      const [status, price] = await Promise.all([
        getIapServerStatus(),
        fetchApplePrice(),
      ]);
      if (cancelled) return;
      setIapServerReady(Boolean(status?.configured));
      setApplePrice(price);
    })();
    return () => { cancelled = true; };
  }, [native]);

  const startAppleSubscribe = async () => {
    setIapLoading(true);
    try {
      await purchasePro();
      await refresh();
      toast.success("Welcome to DAIONE Pro 🎉");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not start purchase";
      // Don't yell at the user if they tapped Cancel on Apple's sheet.
      if (!/cancel/i.test(msg)) toast.error(msg);
    } finally {
      setIapLoading(false);
    }
  };

  const restoreApple = async () => {
    setIapLoading(true);
    try {
      await restoreApplePurchases();
      await refresh();
      toast.success("Purchases restored");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "No purchases to restore");
    } finally {
      setIapLoading(false);
    }
  };

  const startCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setCheckoutLoading(false);
    }
  };

  const openPortal = async () => {
    setCheckoutLoading(true);
    try {
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open Stripe portal");
      setCheckoutLoading(false);
    }
  };

  if (loading && !sub) {
    return (
      <div className="p-10 flex items-center gap-2 text-white/50" data-testid="billing-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading billing…
      </div>
    );
  }

  const plan = sub?.plan || { amount: 9.99, currency: "aud", period_days: 30, name: "DAIONE Pro" };
  const status = sub?.status || "free";
  const daysRemaining = sub?.days_remaining || 0;
  const isPro = sub?.is_pro;
  const inTrial = sub?.in_trial;
  const inPaid = sub?.in_paid;

  const accessEndStr = sub?.access_end
    ? new Date(sub.access_end).toLocaleDateString("en-AU", { year: "numeric", month: "short", day: "numeric" })
    : null;

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto fade-up" data-testid="billing-page">
      <div className="label-eyebrow text-[#6B9BFF] mb-2 flex items-center gap-2">
        <Crown className="w-3.5 h-3.5" /> Billing
      </div>
      <h1 className="font-display text-4xl sm:text-5xl mb-2 leading-none">
        {isPro ? "You're on Pro." : "Upgrade to Pro."}
      </h1>
      <p className="text-white/60 mb-8 max-w-xl">
        {inTrial && !inPaid && "Your free trial is active — no charge yet."}
        {inPaid && "Thanks for supporting DAIONE. Your subscription renews monthly."}
        {!isPro && "Your free trial has ended. Upgrade to unlock AI workouts, photo meal logging, and micro-workouts."}
      </p>

      {/* Polling state */}
      {polling && (
        <div className="border border-[#6B9BFF]/30 bg-[#6B9BFF]/5 p-4 mb-6 flex items-center gap-3" data-testid="billing-polling">
          <Loader2 className="w-4 h-4 animate-spin text-[#6B9BFF]" />
          <span className="text-sm text-white/80">{pollMessage}</span>
        </div>
      )}
      {!polling && pollMessage && (
        <div className="border border-[#FFB020]/30 bg-[#FFB020]/5 p-4 mb-6 text-sm text-white/80" data-testid="billing-poll-message">
          {pollMessage}
        </div>
      )}

      {/* Status card */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-10">
        <div className="lg:col-span-3 border border-white/10 bg-[#121212] p-6" data-testid="billing-status-card">
          <div className="flex items-center justify-between mb-4">
            <div className="label-eyebrow text-white/50">Subscription</div>
            <StatusPill status={status} />
          </div>
          <div className="font-display text-3xl mb-1">{plan.name}</div>
          <div className="text-sm text-white/60 mb-5">
            ${plan.amount.toFixed(2)} {plan.currency.toUpperCase()} / month
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-white/10 pt-4 text-sm">
            <div>
              <div className="label-eyebrow text-white/40 text-[10px] mb-1">
                {inPaid ? "Next renewal" : "Trial ends"}
              </div>
              <div className="text-white font-bold">
                {accessEndStr || "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow text-white/40 text-[10px] mb-1">Days remaining</div>
              <div className="text-white font-bold tabular-nums">
                {isPro ? `${daysRemaining}d` : "0d"}
              </div>
            </div>
          </div>

          {sub?.last_payment_at && (
            <div className="mt-4 pt-4 border-t border-white/10 text-xs text-white/50">
              Last payment: {new Date(sub.last_payment_at).toLocaleString("en-AU")}
            </div>
          )}
        </div>

        {/* Upgrade / Manage CTA */}
        <div className="lg:col-span-2 border border-[#6B9BFF]/30 bg-[#6B9BFF]/5 p-6 flex flex-col" data-testid="billing-cta-card">
          <Crown className="w-6 h-6 text-[#6B9BFF] mb-3" />
          <div className="font-display text-2xl mb-1">${plan.amount.toFixed(2)}</div>
          <div className="text-xs text-white/50 mb-5">{plan.currency.toUpperCase()} per month · cancel anytime</div>

          {native ? (
            // Apple's App Store policy requires IAP for digital subscriptions
            // on iOS. We show the StoreKit purchase button here; the same
            // account remains compatible with Stripe-on-web.
            sub?.has_stripe_subscription ? (
              <div data-testid="billing-native-stripe-active">
                <div className="text-sm text-white/80 mb-3">
                  Your subscription is managed on the web (Stripe).
                </div>
                <p className="text-[11px] text-white/50 leading-relaxed">
                  Sign in on a desktop browser to update your card, view
                  invoices, or cancel. Pro features stay unlocked here either way.
                </p>
              </div>
            ) : iapServerReady === false ? (
              <div data-testid="billing-native-iap-pending">
                <div className="text-sm text-white/80 mb-3">
                  In-app subscriptions are coming soon.
                </div>
                <p className="text-[11px] text-white/50 leading-relaxed">
                  We&apos;re finalising App Store setup. In the meantime, sign in on
                  a desktop browser to start Pro — your account stays in sync.
                </p>
              </div>
            ) : (
              <div data-testid="billing-native-iap-card">
                <button
                  onClick={startAppleSubscribe}
                  disabled={iapLoading || polling}
                  className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2 mb-3 w-full"
                  data-testid="billing-apple-subscribe-button"
                >
                  {iapLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</>
                  ) : (
                    <>{inTrial ? "Upgrade with Apple" : "Subscribe with Apple"} <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
                <p className="text-[11px] text-white/50 leading-relaxed mb-3">
                  {applePrice
                    ? `${applePrice} per month — billed by Apple. Cancel anytime in Settings → Apple ID → Subscriptions.`
                    : "Billed by Apple. Cancel anytime in Settings → Apple ID → Subscriptions."}
                </p>
                <button
                  onClick={restoreApple}
                  disabled={iapLoading}
                  className="text-[11px] text-white/60 hover:text-white underline disabled:opacity-50"
                  data-testid="billing-apple-restore-button"
                >
                  Restore purchases
                </button>
              </div>
            )
          ) : sub?.has_stripe_subscription ? (
            <>
              <button
                onClick={openPortal}
                disabled={checkoutLoading || polling}
                className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2 mb-3"
                data-testid="billing-portal-button"
              >
                {checkoutLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</>
                ) : (
                  <>Manage in Stripe <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
              <p className="text-[11px] text-white/50 leading-relaxed">
                Update your card, view invoices, or cancel — all from Stripe&apos;s secure portal.
              </p>
              {sub.cancel_at_period_end && (
                <p className="mt-3 text-[11px] text-[#FFB020] font-bold uppercase tracking-widest" data-testid="billing-cancel-pending">
                  Cancellation scheduled — Pro access ends on renewal date.
                </p>
              )}
            </>
          ) : (
            <>
              <button
                onClick={startCheckout}
                disabled={checkoutLoading || polling}
                className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2 mb-3"
                data-testid="billing-upgrade-button"
              >
                {checkoutLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Loading…</>
                ) : (
                  <>{inTrial ? "Upgrade now" : "Start Pro"} <ArrowRight className="w-4 h-4" /></>
                )}
              </button>
              <p className="text-[11px] text-white/50 leading-relaxed">
                Secure checkout via Stripe. Your card is never stored on our servers.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Benefits */}
      <div className="border border-white/10 bg-[#121212] p-6 lg:p-8" data-testid="billing-benefits">
        <div className="label-eyebrow text-[#6B9BFF] mb-3">What you unlock</div>
        <h3 className="font-display text-2xl mb-5 leading-none">Pro features</h3>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {BENEFITS.map(({ Icon, label }) => (
            <li key={label} className="flex items-start gap-3 text-sm">
              <div className="w-9 h-9 flex items-center justify-center border border-[#6B9BFF]/40 bg-[#6B9BFF]/5 shrink-0">
                <Icon className="w-4 h-4 text-[#6B9BFF]" />
              </div>
              <div className="text-white/80 pt-1.5">{label}</div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    trialing: { label: "Trial", color: "text-[#6B9BFF]", border: "border-[#6B9BFF]/40", bg: "bg-[#6B9BFF]/5" },
    active:   { label: "Active · Pro", color: "text-[#34C759]", border: "border-[#34C759]/40", bg: "bg-[#34C759]/5" },
    free:     { label: "Trial ended", color: "text-[#FFB020]", border: "border-[#FFB020]/40", bg: "bg-[#FFB020]/5" },
  };
  const cfg = map[status] || map.free;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold border px-2 py-1 ${cfg.color} ${cfg.border} ${cfg.bg}`}
      data-testid="billing-status-pill"
    >
      {cfg.label}
    </span>
  );
}
